import React from 'react';
import _ from 'lodash';

import { PricingQuotePriceOutput } from '../../../lib/pricing-tool-api';

export const MAX_PRICING_QUOTE_PRICES = 5;
export const MIN_SAMPLE_SIZE = 50;
export const MAX_SAMPLE_SIZE = 3000;
export const MIN_QUESTIONS = 12;
export const MAX_QUESTIONS = 75;

export const AT_LEAST_ONE_LOCATION_TOOLTIP =
  'You must have at least one location';

export interface QuoteMatrixProps {
  numberOfQuestions: number[];
  sampleSizes: number[];
  quotes: PricingQuotePriceOutput[];
  calculatedMaxRecommendedSampleSize: number;
}

export async function copyRichContent(
  tableReference: React.RefObject<HTMLElement>,
) {
  const docRange = document.createRange();
  if (tableReference.current) {
    docRange.selectNode(tableReference.current);
    window.getSelection()?.removeAllRanges();
    window.getSelection()?.addRange(docRange);
    document.execCommand('copy');
    window.getSelection()?.removeAllRanges();
  }
}

const QUOTE_TABLE_FONT_FAMILY =
  "'Proxima Nova','Proxima Nova Regular',Helvetica,Arial,sans-serif";
const QUOTE_TABLE_HEADER_COLOR = '#2C609A';
const QUOTE_TABLE_HEADER_TEXT_COLOR = '#ffffff';
const QUOTE_TABLE_BORDER = '1px solid #d9d9d9';

export type QuoteValueSelector = (
  quote: PricingQuotePriceOutput,
) => number | undefined;

export const defaultValueSelector: QuoteValueSelector = (q) => q.rounded_price;

export function sampleSizeLabel(sampleValue: number): string {
  return `n = ${sampleValue.toLocaleString('en-US')}`;
}

export function priceForCell(
  quotes: PricingQuotePriceOutput[],
  questionCount: number,
  sampleValue: number,
  selectValue: QuoteValueSelector,
): string {
  const quote = _.find(
    quotes,
    (q) =>
      q.number_of_questions === questionCount && q.sample_size === sampleValue,
  );
  const value = quote == null ? undefined : selectValue(quote);
  return value == null ? '-' : `$${value.toLocaleString('en-US')}`;
}

export function buildQuoteTableHtml(
  props: QuoteMatrixProps,
  selectValue: QuoteValueSelector = defaultValueSelector,
): string {
  const { numberOfQuestions, sampleSizes, quotes } = props;
  const baseCell = `border: ${QUOTE_TABLE_BORDER}; padding: 8px 16px; text-align: center;`;
  const headerCell = `${baseCell} background-color: ${QUOTE_TABLE_HEADER_COLOR}; color: ${QUOTE_TABLE_HEADER_TEXT_COLOR}; font-weight: bold;`;

  const headerRow = `<tr><td style="${headerCell}"></td>${sampleSizes
    .map((value) => `<td style="${headerCell}">${sampleSizeLabel(value)}</td>`)
    .join('')}</tr>`;

  const bodyRows = numberOfQuestions
    .map((questionCount) => {
      const label = `<td style="${baseCell} font-weight: bold;">${questionCount} Question Poll</td>`;
      const cells = sampleSizes
        .map(
          (sampleValue) =>
            `<td style="${baseCell}">${priceForCell(
              quotes,
              questionCount,
              sampleValue,
              selectValue,
            )}</td>`,
        )
        .join('');
      return `<tr>${label}${cells}</tr>`;
    })
    .join('');

  return `<table style="border-collapse: collapse; font-family: ${QUOTE_TABLE_FONT_FAMILY};"><thead>${headerRow}</thead><tbody>${bodyRows}</tbody></table>`;
}

export function buildQuoteTablePlainText(
  props: QuoteMatrixProps,
  selectValue: QuoteValueSelector = defaultValueSelector,
): string {
  const { numberOfQuestions, sampleSizes, quotes } = props;
  const header = ['', ...sampleSizes.map(sampleSizeLabel)].join('\t');
  const rows = numberOfQuestions.map((questionCount) =>
    [
      `${questionCount} Question Poll`,
      ...sampleSizes.map((sampleValue) =>
        priceForCell(quotes, questionCount, sampleValue, selectValue),
      ),
    ].join('\t'),
  );
  return [header, ...rows].join('\n');
}

export async function copyQuoteTable(
  props: QuoteMatrixProps,
  selectValue: QuoteValueSelector = defaultValueSelector,
): Promise<void> {
  const html = buildQuoteTableHtml(props, selectValue);
  const text = buildQuoteTablePlainText(props, selectValue);

  if (typeof ClipboardItem !== 'undefined' && navigator.clipboard?.write) {
    try {
      await navigator.clipboard.write([
        new ClipboardItem({
          'text/html': new Blob([html], { type: 'text/html' }),
          'text/plain': new Blob([text], { type: 'text/plain' }),
        }),
      ]);
      return;
    } catch {
      // fall through to the selection-based copy below
    }
  }

  const container = document.createElement('div');
  container.innerHTML = html;
  container.setAttribute('contenteditable', 'true');
  container.style.position = 'fixed';
  container.style.left = '-9999px';
  document.body.appendChild(container);
  const selection = window.getSelection();
  try {
    const docRange = document.createRange();
    docRange.selectNodeContents(container);
    selection?.removeAllRanges();
    selection?.addRange(docRange);
    if (!document.execCommand('copy')) {
      throw new Error('Unable to copy the table to the clipboard');
    }
  } finally {
    selection?.removeAllRanges();
    document.body.removeChild(container);
  }
}
